// Shared RPM reading formatters — used by the Device Readings table/CSV, the
// exported/shared PDF (generateRpmReadingsPdf), and the public report page.
// Keeping one formatter guarantees the clinician preview and the patient-facing
// document render identical text.

/**
 * Human-friendly label for a raw reading_type value.
 * Falls back to inserting spaces before capitals for unknown types.
 */
export function readingTypeLabel(type: string): string {
  switch (type) {
    case 'BloodPressure':
      return 'Blood Pressure';
    case 'BloodGlucose':
      return 'Blood Glucose';
    case 'SpO2':
      return 'SpO₂';
    case 'PeakFlow':
      return 'Peak Flow';
    case 'Weight':
      return 'Weight';
    case 'Temperature':
      return 'Temperature';
    default:
      // "SomeType" -> "Some Type"
      return type.replace(/([a-z])([A-Z])/g, '$1 $2');
  }
}

/**
 * Renders a reading's data blob into a compact, user-friendly "Measured Results"
 * string per reading_type. Intentionally omits any device/patient identifiers.
 */
export function formatReadingData(type: string, data: Record<string, any>): string {
  switch (type) {
    case 'BloodPressure':
      return `${data.systolic}/${data.diastolic} mmHg, pulse ${data.pulse}${data.arrhythmia ? ' (ARR)' : ''}`;
    case 'BloodGlucose':
      return `${data.glucose} ${data.unit || 'mg/dL'} (${data.readingPeriod || ''})`;
    case 'Weight':
      return `${data.weight} ${data.unit || 'lb'}`;
    case 'SpO2':
      return `SpO2 ${data.spo2}%, pulse ${data.pulse}`;
    case 'Temperature':
      return `${data.temperature} ${data.unit || 'F'}`;
    case 'PeakFlow':
      return `FEV1 ${data.fev1}L, PEF ${data.pef}`;
    default:
      return JSON.stringify(data);
  }
}
